/**
 * PAUSES, as the page sees them — the lifecycle/visibility evidence a render loop's measurement needs
 * (renderPolicy.ts DetailGovernor `settled`). While the page is hidden (a minimized window, a backgrounded app's
 * WebView, a TV shell hiding its view) or frozen, requestAnimationFrame stops; the first interval after it is wall
 * time, not a frame, and must never be scored as the world's cost. An interval's length can't tell a pause from a
 * world that really takes seconds per frame — the page's own lifecycle events can.
 */
export interface PauseWatch {
  /** Did the interval closing now span a pause (the page hidden or frozen at any point since the last call, or
   *  hidden now)? Each call closes that interval. */
  spanned: () => boolean
  dispose: () => void
}

export function createPauseWatch(doc: Document = document, win: Window = window): PauseWatch {
  let paused = doc.visibilityState === 'hidden'
  const mark = (): void => { paused = true }
  const onVisibility = (): void => { if (doc.visibilityState === 'hidden') paused = true }
  doc.addEventListener('visibilitychange', onVisibility)
  doc.addEventListener('freeze', mark) // Page Lifecycle: a frozen page runs no frames
  win.addEventListener('pagehide', mark)
  return {
    spanned: () => {
      const was = paused || doc.visibilityState === 'hidden'
      paused = false
      return was
    },
    dispose: () => {
      doc.removeEventListener('visibilitychange', onVisibility)
      doc.removeEventListener('freeze', mark)
      win.removeEventListener('pagehide', mark)
    }
  }
}
